import {
	ArrowSquareOut,
	DownloadSimple,
	MagnifyingGlassMinus,
	MagnifyingGlassPlus,
	ShareNetwork,
} from '@phosphor-icons/react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal/Modal';
import { PaperSkeleton, PdfPages, usePinchZoom, type PdfFile } from './PdfDocument';
import {
	downloadBlob,
	downloadServerFile,
	isLocalFileUrl,
	openExternalUrl,
	readLocalFile,
	type ServerFileTarget,
} from '../../shared/download';
import { isNativeApp } from '../../shared/utils/nativeBridge';
import Loading from '../atoms/Loading';
import {
	resolveUrl,
	isGoogleStorageUrl,
} from '../../shared/utils/signedUrl.util';
import { useSignedUrlDownload } from '../../hooks/useSignedUrlDownload';
import { Spinner } from '../ui/spinner';
import useEvents from '../../hooks/useEvents';
import { EVENT_TYPES } from '../../shared/constants/events';

const VIEWER_LOAD_TIMEOUT_MS = 15000;

const PDF_ZOOM_IN = 2;

export type ModalFramePdf = {
	title: string;
	subtitle?: string;
	icon?: ReactNode;
	fileName: string;
	file?: PdfFile;
	/** The file can be looked at but not saved from here (the native app has no server copy of it). */
	viewOnly?: boolean;
};

/** The file a server-backed download is built from: what it asks for, and the name a browser saves it under. */
export type ModalFrameServerFile = { target: ServerFileTarget; fileName: string };

const fallbackOf = (file?: PdfFile) => {
	if (file instanceof Blob) return () => file;
	return file && isLocalFileUrl(file) ? () => readLocalFile(file) : undefined;
};

const getViewerContext = () => {
	const ua = window.navigator.userAgent;
	const platform = /iPhone|iPad|iPod/i.test(ua)
		? 'ios'
		: /Android/i.test(ua)
			? 'android'
			: 'desktop';
	const isStandalonePwa =
		window.matchMedia?.('(display-mode: standalone)').matches ||
		(window.navigator as { standalone?: boolean }).standalone === true;
	return {
		platform,
		is_mobile: platform !== 'desktop',
		is_pwa_standalone: Boolean(isStandalonePwa),
	};
};

const getSrcHost = (url: string): string => {
	try {
		return new URL(url).hostname;
	} catch {
		return 'invalid_url';
	}
};

/**
 * Canonical document / file viewer for the whole app. Embeds a URL (PDF,
 * article, signed GCS doc) in an iframe with the actions row — "Navegador"
 * (open in browser) and "Descargar" — rendered BELOW the frame, plus signed
 * URL resolution, a Google-docs PDF fallback (`forceViewAsPDF`) and load
 * tracking. For PDFs pass `forceViewAsPDF showDownloadButton`. Do not build a
 * parallel viewer — extend this one.
 *
 * With `pdf`, the file is drawn page by page with pdf.js under a header with
 * its title, with Descargar and Compartir as full-width actions: the same on
 * every phone, without the browser's own PDF viewer.
 */
const ModalFrame = ({
	open,
	onClose,
	src,
	showOpenInBrowser = true,
	showDownloadButton = false,
	forceViewAsPDF = false,
	onOpenInBrowser,
	onDownload,
	onShare,
	pdf,
	serverFile,
}: {
	open: boolean;
	onClose: () => void;
	src: string;
	showOpenInBrowser?: boolean;
	showDownloadButton?: boolean;
	forceViewAsPDF?: boolean;
	onOpenInBrowser?: () => void;
	onDownload?: () => void;
	onShare?: () => void;
	pdf?: ModalFramePdf;
	/** Inside the native app a local `blob:` can't be saved: Descargar asks the server for a link to this file instead. */
	serverFile?: ModalFrameServerFile;
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [zoom, setZoom] = useState(1);
	const pdfScroller = useRef<HTMLDivElement>(null);
	const pdfPages = useRef<HTMLDivElement>(null);
	usePinchZoom(pdfScroller, pdfPages, zoom, setZoom, open && Boolean(pdf?.file));
	// The iframe waits for the resolved URL: fed the raw `gs://` or unsigned link, the
	// Google viewer paints its error page and its load hides the spinner too early.
	const [resolved, setResolved] = useState<{ src: string; url: string } | null>(null);
	const resolvedSrc = resolved?.src === src ? resolved.url : '';
	const { trackEvent } = useEvents();
	const loadStartRef = useRef<number | null>(null);
	const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const signedUrlResolvedRef = useRef<boolean>(false);

	useEffect(() => {
		if (!src || !open) return;

		let stale = false;
		setIsLoading(true);
		setResolved(null);
		loadStartRef.current = performance.now();
		signedUrlResolvedRef.current = false;

		if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
		loadTimeoutRef.current = setTimeout(() => {
			if (loadStartRef.current === null) return;
			trackEvent({
				eventName: EVENT_TYPES.DOCUMENT_VIEWER_FAILED,
				eventPayload: {
					reason: 'load_timeout',
					duration_ms: Math.round(performance.now() - loadStartRef.current),
					forced_pdf_viewer: forceViewAsPDF,
					is_google_storage_url: isGoogleStorageUrl(src),
					signed_url_resolved: signedUrlResolvedRef.current,
					src_host: getSrcHost(src),
					...getViewerContext(),
				},
			});
			loadStartRef.current = null;
		}, VIEWER_LOAD_TIMEOUT_MS);

		resolveUrl(src)
			.then((url) => {
				if (stale) return;
				signedUrlResolvedRef.current = url !== src;
				setResolved({ src, url });
			})
			.catch(() => {
				if (stale) return;
				trackEvent({
					eventName: EVENT_TYPES.DOCUMENT_VIEWER_FAILED,
					eventPayload: {
						reason: 'signed_url_failed',
						duration_ms:
							loadStartRef.current !== null
								? Math.round(performance.now() - loadStartRef.current)
								: 0,
						forced_pdf_viewer: forceViewAsPDF,
						is_google_storage_url: isGoogleStorageUrl(src),
						signed_url_resolved: false,
						src_host: getSrcHost(src),
						...getViewerContext(),
					},
				});
				setResolved({ src, url: src });
			});

		return () => {
			stale = true;
			if (loadTimeoutRef.current) {
				clearTimeout(loadTimeoutRef.current);
				loadTimeoutRef.current = null;
			}
		};
	}, [src, open, forceViewAsPDF, trackEvent]);

	const pdfViewerUrl = useMemo(() => {
		if (!resolvedSrc) return '';
		return `https://docs.google.com/viewer?url=${encodeURIComponent(resolvedSrc)}&embedded=true&datetime=${new Date().toISOString()}`;
	}, [resolvedSrc]);

	const generatePDFViewerUrl = (url: string) => {
		return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true&datetime=${new Date().toISOString()}`;
	};

	const handleIframeLoad = () => {
		setIsLoading(false);
		if (loadStartRef.current === null) return;
		trackEvent({
			eventName: EVENT_TYPES.DOCUMENT_VIEWER_LOADED,
			eventPayload: {
				duration_ms: Math.round(performance.now() - loadStartRef.current),
				forced_pdf_viewer: forceViewAsPDF,
				is_google_storage_url: isGoogleStorageUrl(src),
				signed_url_resolved: signedUrlResolvedRef.current,
				src_host: getSrcHost(src),
				...getViewerContext(),
			},
		});
		loadStartRef.current = null;
		if (loadTimeoutRef.current) {
			clearTimeout(loadTimeoutRef.current);
			loadTimeoutRef.current = null;
		}
	};

	const handleViewerFailed = (error: Error) => {
		if (loadStartRef.current === null) return;
		trackEvent({
			eventName: EVENT_TYPES.DOCUMENT_VIEWER_FAILED,
			eventPayload: {
				reason: 'render_failed',
				error_message: error.message,
				duration_ms: Math.round(performance.now() - loadStartRef.current),
				forced_pdf_viewer: forceViewAsPDF,
				is_google_storage_url: isGoogleStorageUrl(src),
				signed_url_resolved: signedUrlResolvedRef.current,
				src_host: getSrcHost(src),
				...getViewerContext(),
			},
		});
		loadStartRef.current = null;
		if (loadTimeoutRef.current) {
			clearTimeout(loadTimeoutRef.current);
			loadTimeoutRef.current = null;
		}
	};

	const { download, isAnyDownloading } = useSignedUrlDownload();

	const downloadFromServer = (file: ModalFrameServerFile, fallback?: () => Blob | Promise<Blob>) =>
		void downloadServerFile(file.target, { fileName: file.fileName, fallback });

	const handleOpenInBrowser = () => {
		onOpenInBrowser?.();
		if (serverFile && isNativeApp() && isLocalFileUrl(resolvedSrc)) {
			downloadFromServer(serverFile);
			return;
		}
		openExternalUrl(forceViewAsPDF ? generatePDFViewerUrl(resolvedSrc) : resolvedSrc);
	};

	const handleDownload = () => {
		onDownload?.();
		if (serverFile) downloadFromServer(serverFile, fallbackOf(src));
		else download(src);
	};

	if (pdf) {
		const handlePdfDownload = () => {
			onDownload?.();
			if (serverFile) downloadFromServer(serverFile, fallbackOf(pdf.file));
			else if (pdf.file instanceof Blob) void downloadBlob({ blob: pdf.file, fileName: pdf.fileName });
			else download(src);
		};
		const handlePdfClose = () => {
			setZoom(1);
			onClose();
		};
		const ready = pdf.file !== undefined;
		const canDownload = !pdf.viewOnly;
		const zoomedIn = zoom > 1;
		return (
			<Modal
				open={open}
				onClose={handlePdfClose}
				fullScreenOnMobile
				sx='!p-0 h-[88dvh] !max-w-[min(92dvw,760px)]'
			>
				<div className='flex h-full min-h-0 flex-col'>
					<header className='flex items-center gap-3 px-4 pt-4 pb-3 pr-14 md:px-5'>
						{pdf.icon && (
							<span className='bg-h-primary-95 text-h-primary-50 flex size-10 shrink-0 items-center justify-center rounded-xl'>
								{pdf.icon}
							</span>
						)}
						<span className='flex min-w-0 flex-col'>
							<span className='text-h-text-heading truncate text-[17px] leading-tight font-bold'>
								{pdf.title}
							</span>
							{pdf.subtitle && (
								<span className='text-h-text-medium truncate text-[13px] first-letter:uppercase'>
									{pdf.subtitle}
								</span>
							)}
						</span>
					</header>
					<div className='relative min-h-0 flex-1'>
						<div
							ref={pdfScroller}
							className='bg-h-bg-app absolute inset-0 touch-pan-x touch-pan-y overflow-auto overscroll-contain p-3 md:p-4'
						>
							{pdf.file ? (
								<div ref={pdfPages}>
									<PdfPages
										file={pdf.file}
										zoom={zoom}
										onLoaded={handleIframeLoad}
										onFailed={handleViewerFailed}
									/>
								</div>
							) : (
								<PaperSkeleton />
							)}
						</div>
						{ready && (
							<button
								type='button'
								aria-label={zoomedIn ? 'Alejar' : 'Acercar'}
								onClick={() => setZoom(zoomedIn ? 1 : PDF_ZOOM_IN)}
								className='text-h-text-heading absolute right-3 bottom-3 flex size-11 items-center justify-center rounded-full bg-white shadow-md active:scale-95'
							>
								{zoomedIn ? (
									<MagnifyingGlassMinus
										size={20}
										weight='bold'
									/>
								) : (
									<MagnifyingGlassPlus
										size={20}
										weight='bold'
									/>
								)}
							</button>
						)}
					</div>
					{(canDownload || onShare) && (
						<footer
							className={`grid gap-2 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-5 ${onShare && canDownload ? 'grid-cols-2' : 'grid-cols-1'}`}
						>
							{canDownload && (
								<button
									type='button'
									onClick={handlePdfDownload}
									disabled={!ready || isAnyDownloading}
									className={`flex h-12 items-center justify-center gap-2 rounded-2xl text-base font-semibold active:scale-[0.98] disabled:opacity-50 ${onShare ? 'bg-h-primary-95 text-h-primary-50' : 'bg-h-primary-50 text-white'}`}
								>
									{isAnyDownloading ? (
										<Spinner className='size-4' />
									) : (
										<DownloadSimple
											size={20}
											weight='bold'
										/>
									)}
									Descargar
								</button>
							)}
							{onShare && (
								<button
									type='button'
									onClick={onShare}
									className='bg-h-primary-50 flex h-12 items-center justify-center gap-2 rounded-2xl text-base font-semibold text-white active:scale-[0.98]'
								>
									<ShareNetwork
										size={20}
										weight='bold'
									/>
									Compartir
								</button>
							)}
						</footer>
					)}
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx='!p-3 !max-w-[min(90dvw,850px)]'
		>
			<div className='w-full h-[68dvh] md:h-[calc(100vh-150px)] relative rounded-lg overflow-hidden border border-dashed border-h-neutral-60'>
				{resolvedSrc && (
					<iframe
						key={src}
						title='Embedded Page'
						width='100%'
						height='100%'
						src={forceViewAsPDF ? pdfViewerUrl : resolvedSrc}
						allowFullScreen
						onLoad={handleIframeLoad}
						className={`transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
					></iframe>
				)}
				{isLoading && (
					<div className='absolute inset-0 z-10 flex items-center justify-center pointer-events-none'>
						<Loading size={32} />
					</div>
				)}
			</div>

			<div className='flex items-center gap-2 mt-3 justify-center'>
				{showOpenInBrowser && (
					<button
						onClick={handleOpenInBrowser}
						disabled={!resolvedSrc}
						className='bg-h-primary-90 text-h-primary rounded-lg px-3 flex items-center justify-center h-8 gap-1 text-sm'
					>
						<ArrowSquareOut
							size={14}
							weight='bold'
							className='inline-block mb-0.5'
						/>
						Navegador{' '}
					</button>
				)}
				{showDownloadButton && (
					<button
						className='bg-h-primary-90 text-h-primary rounded-lg px-3 flex items-center justify-center h-8 gap-1 text-sm'
						onClick={handleDownload}
						disabled={isAnyDownloading}
					>
						{isAnyDownloading ? (
							<Spinner className='size-3.5' />
						) : (
							<DownloadSimple
								size={14}
								weight='bold'
							/>
						)}
						Descargar
					</button>
				)}
				{onShare && (
					<button
						className='bg-h-primary-50 rounded-lg px-3 flex items-center justify-center h-8 gap-1 text-sm text-white'
						onClick={onShare}
					>
						<ShareNetwork
							size={14}
							weight='bold'
						/>
						Compartir
					</button>
				)}
			</div>
		</Modal>
	);
};

export default ModalFrame;
